// Custom resolver to remove <p> tags from description/body/paragraph etc. 
// Any Storyblok fields that use the Richtext field should render properly with this
// For example on the FiftyFifty component, here are the imports:
// import { storyblokEditable } from "@storyblok/react";
// import { useState, useEffect } from "react";
// import { customRenderRichText } from "../utils/renderRichText";
// And then this properly displays the Richtext without HTML tags 
// {/* Richtext Description Paragraph */}
// <div className="text-sm md:text-base leading-relaxed mb-6 font-['Roboto']">
// {customRenderRichText(blok.description)}
// </div>
// Applies for all components that require richtext

export const customRenderRichText = (richText) => {
  if (!richText || !richText.content) return null;

  const renderNode = (node, index) => {
    switch (node.type) {
      // Make each paragraph its own block with a margin-bottom
      case "paragraph":
        return (
          <div key={index} style={{ marginBottom: "0.75rem" }}>
            {node.content?.map((child, childIdx) =>
              renderNode(child, `${index}-${childIdx}`)
            )}
          </div>
        );

      case "text": {
        let content = node.text;

        if (node.marks?.length) {
          node.marks.forEach((mark) => {
            if (mark.type === "bold") {
              content = <strong key={index}>{content}</strong>;
            }
            if (mark.type === "italic") {
              content = <em key={index}>{content}</em>;
            }
            if (mark.type === "link") {
              content = (
                <a
                  key={index}
                  href={mark.attrs.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {content}
                </a>
              );
            }
          });
        }

        return content;
      }

      // Bullet & ordered lists
      case "bullet_list":
      case "ordered_list":
        return (
          <div key={index} style={{ marginTop: "0.75rem" }}>
            {node.content?.map((listItemNode, liIdx) => (
              <div key={liIdx} style={{ display: "flex" }}>
                <span style={{ marginRight: "0.5rem" }}>•</span>
                <span>
                  {listItemNode.content?.map((child, childIdx) =>
                    renderNode(child, `${index}-${liIdx}-${childIdx}`)
                  )}
                </span>
              </div>
            ))}
          </div>
        );

      case "list_item":
      case "listItem":
        return (
          <span key={index}>{node.content?.map(renderNode)}</span>
        );

      // Shift+Enter in Storyblok creates a hard_break
      case "hard_break":
        return <br key={index} />;

      case "heading": {
        const level = node.attrs?.level || 2;
        const HeadingTag = `h${level}`;
        return (
          <HeadingTag key={index}>
            {node.content?.map(renderNode)}
          </HeadingTag>
        );
      }

      default:
        return (
          <span key={index}>
            {node.content ? node.content.map(renderNode) : null}
          </span>
        );
    }
  };

  return <>{richText.content.map(renderNode)}</>;
};
